import { useState, Fragment } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Transition, Listbox, Tab } from '@headlessui/react';
import {
  PlusIcon,
  ChevronUpDownIcon,
  CheckIcon,
  MapPinIcon,
  PencilSquareIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  GlobeAmericasIcon,
  TagIcon,
  FolderIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { stateComplianceApi } from '../lib/api';
import { useTeamId } from '../hooks/useTeamId';
import FeatureGate from '../components/FeatureGate';
import clsx from 'clsx';

const PRIORITY_OPTIONS = [
  { value: 'LOW', label: 'Low' },
  { value: 'MEDIUM', label: 'Medium' },
  { value: 'HIGH', label: 'High' },
  { value: 'CRITICAL', label: 'Critical' },
];

const PRIORITY_BADGE: Record<string, { label: string; className: string }> = {
  CRITICAL: { label: 'Critical', className: 'bg-red-100 text-red-700' },
  HIGH: { label: 'High', className: 'bg-orange-100 text-orange-700' },
  MEDIUM: { label: 'Medium', className: 'bg-yellow-100 text-yellow-700' },
  LOW: { label: 'Low', className: 'bg-green-100 text-green-700' },
};

const EMPTY_FORM = {
  stateCode: '',
  stateName: '',
  category: '',
  ruleName: '',
  description: '',
  citation: '',
  sourceUrl: '',
  applicableProducts: '',
  complianceCheckpoints: '',
  penaltyInfo: '',
  priority: 'MEDIUM',
  effectiveDate: '',
};

export default function StateCompliancePage() {
  const queryClient = useQueryClient();
  const [stateFilter, setStateFilter] = useState('');
  const [categorySearch, setCategorySearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [byStateCode, setByStateCode] = useState('');

  // Team
  const { teamId } = useTeamId();

  // List rules
  const { data: listData, isLoading } = useQuery({
    queryKey: ['stateRules', teamId, stateFilter, categorySearch],
    queryFn: () =>
      stateComplianceApi.list(teamId!, {
        stateCode: stateFilter || undefined,
        category: categorySearch || undefined,
      }),
    enabled: !!teamId,
  });
  const rules = listData?.data?.rules || [];
  const total = listData?.data?.total || 0;

  // Summary
  const { data: summaryData } = useQuery({
    queryKey: ['stateComplianceSummary', teamId],
    queryFn: () => stateComplianceApi.getSummary(teamId!),
    enabled: !!teamId,
  });
  const summary = summaryData?.data;

  // By State
  const { data: byStateData } = useQuery({
    queryKey: ['stateComplianceByState', teamId, byStateCode],
    queryFn: () => stateComplianceApi.getByState(teamId!, byStateCode),
    enabled: !!teamId && !!byStateCode,
  });
  const byStateRules = byStateData?.data?.rules || [];

  // Unique state codes for filter dropdown
  const stateCodeOptions: string[] = summary?.states
    ? summary.states.map((s: any) => s.stateCode || s.state)
    : [];

  const STATE_FILTERS = [
    { value: '', label: 'All States' },
    ...stateCodeOptions.map((s: string) => ({ value: s, label: s })),
  ];

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data: any) => stateComplianceApi.create(teamId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stateRules'] });
      queryClient.invalidateQueries({ queryKey: ['stateComplianceSummary'] });
      queryClient.invalidateQueries({ queryKey: ['stateComplianceByState'] });
      toast.success('State compliance rule created successfully');
      setShowForm(false);
      setFormData(EMPTY_FORM);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to create rule');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      stateComplianceApi.update(teamId!, id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stateRules'] });
      queryClient.invalidateQueries({ queryKey: ['stateComplianceSummary'] });
      queryClient.invalidateQueries({ queryKey: ['stateComplianceByState'] });
      toast.success('Rule updated successfully');
      setShowForm(false);
      setEditId(null);
      setFormData(EMPTY_FORM);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to update rule');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => stateComplianceApi.delete(teamId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stateRules'] });
      queryClient.invalidateQueries({ queryKey: ['stateComplianceSummary'] });
      queryClient.invalidateQueries({ queryKey: ['stateComplianceByState'] });
      toast.success('Rule deleted successfully');
      setDeleteId(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.error || 'Failed to delete rule');
    },
  });

  const handleSubmit = () => {
    const payload = {
      stateCode: formData.stateCode.toUpperCase().trim(),
      stateName: formData.stateName,
      category: formData.category,
      ruleName: formData.ruleName,
      description: formData.description || undefined,
      citation: formData.citation || undefined,
      sourceUrl: formData.sourceUrl || undefined,
      applicableProducts: formData.applicableProducts
        ? formData.applicableProducts.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
      complianceCheckpoints: formData.complianceCheckpoints
        ? formData.complianceCheckpoints.split(',').map((s) => s.trim()).filter(Boolean)
        : undefined,
      penaltyInfo: formData.penaltyInfo || undefined,
      priority: formData.priority,
      effectiveDate: formData.effectiveDate || undefined,
    };

    if (editId) {
      updateMutation.mutate({ id: editId, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const openEdit = (rule: any) => {
    setEditId(rule.id);
    setFormData({
      stateCode: rule.stateCode || '',
      stateName: rule.stateName || '',
      category: rule.category || '',
      ruleName: rule.ruleName || '',
      description: rule.description || '',
      citation: rule.citation || '',
      sourceUrl: rule.sourceUrl || '',
      applicableProducts: rule.applicableProducts
        ? rule.applicableProducts.join(', ')
        : '',
      complianceCheckpoints: rule.complianceCheckpoints
        ? rule.complianceCheckpoints.join(', ')
        : '',
      penaltyInfo: rule.penaltyInfo || '',
      priority: rule.priority || 'MEDIUM',
      effectiveDate: rule.effectiveDate
        ? new Date(rule.effectiveDate).toISOString().split('T')[0]
        : '',
    });
    setShowForm(true);
  };

  const openCreate = () => {
    setEditId(null);
    setFormData(EMPTY_FORM);
    setShowForm(true);
  };

  // Summary counts
  const totalRules = summary?.totalRules || total;
  const statesCovered = summary?.statesCovered || stateCodeOptions.length;
  const highPriorityRules = summary?.highPriorityRules || rules.filter(
    (r: any) => r.priority === 'HIGH' || r.priority === 'CRITICAL'
  ).length;
  const totalCategories = summary?.totalCategories || 0;

  // Group by-state rules by category
  const byStateGrouped: Record<string, any[]> = {};
  byStateRules.forEach((r: any) => {
    const cat = r.category || 'Uncategorized';
    if (!byStateGrouped[cat]) byStateGrouped[cat] = [];
    byStateGrouped[cat].push(r);
  });

  return (
    <FeatureGate feature="state_compliance" mode="lock">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">State-Specific Rules</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage state-specific compliance rules, requirements, and regulations
            </p>
          </div>
          <button
            onClick={openCreate}
            className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700"
          >
            <PlusIcon className="h-4 w-4" />
            Add Rule
          </button>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-blue-50 p-2">
                <FolderIcon className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Rules</p>
                <p className="text-2xl font-bold text-gray-900">{totalRules}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-green-50 p-2">
                <GlobeAmericasIcon className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">States Covered</p>
                <p className="text-2xl font-bold text-gray-900">{statesCovered}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-orange-50 p-2">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">High Priority Rules</p>
                <p className="text-2xl font-bold text-orange-600">{highPriorityRules}</p>
              </div>
            </div>
          </div>
          <div className="rounded-lg border bg-white p-4">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-purple-50 p-2">
                <TagIcon className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Categories</p>
                <p className="text-2xl font-bold text-gray-900">{totalCategories}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tab.Group>
          <Tab.List className="flex gap-4 border-b">
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Rules
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              Summary
            </Tab>
            <Tab
              className={({ selected }) =>
                clsx(
                  'border-b-2 px-1 py-2 text-sm font-medium',
                  selected
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                )
              }
            >
              By State
            </Tab>
          </Tab.List>

          <Tab.Panels>
            {/* Rules List Tab */}
            <Tab.Panel className="space-y-4 pt-4">
              {/* Filters */}
              <div className="flex flex-wrap items-center gap-3">
                <Listbox value={stateFilter} onChange={setStateFilter}>
                  <div className="relative w-44">
                    <Listbox.Button className="relative w-full cursor-pointer rounded-lg border bg-white py-2 pl-3 pr-10 text-left text-sm">
                      <span>
                        {STATE_FILTERS.find((f) => f.value === stateFilter)?.label}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5">
                        {STATE_FILTERS.map((f) => (
                          <Listbox.Option
                            key={f.value}
                            value={f.value}
                            className={({ active }) =>
                              clsx(
                                'cursor-pointer select-none py-2 pl-10 pr-4 text-sm',
                                active ? 'bg-blue-50 text-blue-700' : 'text-gray-900'
                              )
                            }
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={clsx(
                                    'block truncate',
                                    selected && 'font-medium'
                                  )}
                                >
                                  {f.label}
                                </span>
                                {selected && (
                                  <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                    <CheckIcon className="h-4 w-4" />
                                  </span>
                                )}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>

                <input
                  type="text"
                  placeholder="Search by category..."
                  value={categorySearch}
                  onChange={(e) => setCategorySearch(e.target.value)}
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {/* Rules table */}
              <div className="rounded-lg border bg-white">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
                  </div>
                ) : rules.length === 0 ? (
                  <div className="py-12 text-center text-gray-500">
                    <MapPinIcon className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-lg font-medium">No state compliance rules found</p>
                    <p className="mt-1 text-sm">
                      Add your first state compliance rule to get started.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            State
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Category
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Rule Name
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Priority
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Effective Date
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {rules.map((rule: any) => {
                          const priorityCfg =
                            PRIORITY_BADGE[rule.priority] || PRIORITY_BADGE.LOW;
                          return (
                            <tr key={rule.id} className="hover:bg-gray-50">
                              <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                                {rule.stateCode}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {rule.category}
                              </td>
                              <td className="max-w-xs truncate px-6 py-4 text-sm text-gray-900">
                                {rule.ruleName}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <span
                                  className={clsx(
                                    'inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium',
                                    priorityCfg.className
                                  )}
                                >
                                  {priorityCfg.label}
                                </span>
                              </td>
                              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                                {rule.effectiveDate
                                  ? new Date(rule.effectiveDate).toLocaleDateString()
                                  : '--'}
                              </td>
                              <td className="whitespace-nowrap px-6 py-4">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => openEdit(rule)}
                                    className="text-sm font-medium text-blue-600 hover:text-blue-800"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => setDeleteId(rule.id)}
                                    className="text-sm font-medium text-red-600 hover:text-red-800"
                                  >
                                    Delete
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </Tab.Panel>

            {/* Summary Tab */}
            <Tab.Panel className="space-y-6 pt-4">
              {summary && summary.states && summary.states.length > 0 ? (
                <div className="rounded-lg border bg-white p-6">
                  <h3 className="text-lg font-semibold text-gray-900">
                    States Coverage
                  </h3>
                  <div className="mt-4 overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            State
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Rules
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            Categories
                          </th>
                          <th className="px-4 py-2 text-left text-xs font-medium uppercase text-gray-500">
                            High Priority Count
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {summary.states.map((s: any, idx: number) => (
                          <tr key={idx}>
                            <td className="whitespace-nowrap px-4 py-2 text-sm font-medium text-gray-900">
                              {s.stateCode || s.state}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                              {s.ruleCount || s.rules || s.count}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                              {s.categoryCount || s.categories}
                            </td>
                            <td className="whitespace-nowrap px-4 py-2 text-sm text-gray-700">
                              <span
                                className={clsx(
                                  (s.highPriorityCount || s.highPriority || 0) > 0
                                    ? 'font-medium text-orange-600'
                                    : 'text-gray-500'
                                )}
                              >
                                {s.highPriorityCount || s.highPriority || 0}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  <GlobeAmericasIcon className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-4 text-lg font-medium">No summary data available</p>
                  <p className="mt-1 text-sm">
                    Add state compliance rules to see a summary breakdown.
                  </p>
                </div>
              )}
            </Tab.Panel>

            {/* By State Tab */}
            <Tab.Panel className="space-y-4 pt-4">
              <div className="flex items-center gap-3">
                <label className="text-sm font-medium text-gray-700">
                  Select State Code:
                </label>
                <input
                  type="text"
                  placeholder="e.g., CA, TX, NY"
                  value={byStateCode}
                  onChange={(e) => setByStateCode(e.target.value.toUpperCase().trim())}
                  maxLength={2}
                  className="w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase focus:border-blue-500 focus:ring-blue-500"
                />
              </div>

              {byStateCode && byStateCode.length === 2 ? (
                byStateRules.length > 0 ? (
                  <div className="space-y-6">
                    {Object.entries(byStateGrouped).map(([category, catRules]) => (
                      <div key={category} className="rounded-lg border bg-white p-6">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {category}
                        </h3>
                        <div className="mt-4 space-y-3">
                          {(catRules as any[]).map((rule: any) => {
                            const priorityCfg =
                              PRIORITY_BADGE[rule.priority] || PRIORITY_BADGE.LOW;
                            return (
                              <div
                                key={rule.id}
                                className="flex items-start justify-between rounded-lg border border-gray-100 bg-gray-50 p-4"
                              >
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <p className="text-sm font-medium text-gray-900">
                                      {rule.ruleName}
                                    </p>
                                    <span
                                      className={clsx(
                                        'inline-flex rounded-full px-2 py-0.5 text-xs font-medium',
                                        priorityCfg.className
                                      )}
                                    >
                                      {priorityCfg.label}
                                    </span>
                                  </div>
                                  {rule.description && (
                                    <p className="mt-1 text-sm text-gray-500">
                                      {rule.description}
                                    </p>
                                  )}
                                  {rule.citation && (
                                    <p className="mt-1 text-xs text-gray-400">
                                      Citation: {rule.citation}
                                    </p>
                                  )}
                                  {rule.effectiveDate && (
                                    <p className="mt-1 text-xs text-gray-400">
                                      Effective:{' '}
                                      {new Date(
                                        rule.effectiveDate
                                      ).toLocaleDateString()}
                                    </p>
                                  )}
                                </div>
                                <div className="ml-4 flex items-center gap-1">
                                  <button
                                    onClick={() => openEdit(rule)}
                                    className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-blue-600"
                                  >
                                    <PencilSquareIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteId(rule.id)}
                                    className="rounded p-1 text-gray-400 hover:bg-gray-200 hover:text-red-600"
                                  >
                                    <TrashIcon className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="py-12 text-center text-gray-500">
                    <MapPinIcon className="mx-auto h-12 w-12 text-gray-300" />
                    <p className="mt-4 text-lg font-medium">
                      No rules found for {byStateCode}
                    </p>
                    <p className="mt-1 text-sm">
                      Add rules for this state to see them here.
                    </p>
                  </div>
                )
              ) : (
                <div className="py-12 text-center text-gray-500">
                  <GlobeAmericasIcon className="mx-auto h-12 w-12 text-gray-300" />
                  <p className="mt-4 text-lg font-medium">
                    Enter a 2-letter state code above
                  </p>
                  <p className="mt-1 text-sm">
                    View all compliance rules grouped by category for a specific state.
                  </p>
                </div>
              )}
            </Tab.Panel>
          </Tab.Panels>
        </Tab.Group>

        {/* Create/Edit Rule Modal */}
        <Transition appear show={showForm} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => {
              setShowForm(false);
              setEditId(null);
              setFormData(EMPTY_FORM);
            }}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      {editId ? 'Edit Rule' : 'Add State Compliance Rule'}
                    </Dialog.Title>
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            State Code (2-letter)
                          </label>
                          <input
                            type="text"
                            value={formData.stateCode}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                stateCode: e.target.value
                                  .toUpperCase()
                                  .slice(0, 2),
                              })
                            }
                            placeholder="e.g., CA"
                            maxLength={2}
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm uppercase focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            State Name
                          </label>
                          <input
                            type="text"
                            value={formData.stateName}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                stateName: e.target.value,
                              })
                            }
                            placeholder="e.g., California"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Category
                          </label>
                          <input
                            type="text"
                            value={formData.category}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                category: e.target.value,
                              })
                            }
                            placeholder="e.g., Lending, Privacy"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Priority
                          </label>
                          <select
                            value={formData.priority}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                priority: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          >
                            {PRIORITY_OPTIONS.map((p) => (
                              <option key={p.value} value={p.value}>
                                {p.label}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Rule Name
                        </label>
                        <input
                          type="text"
                          value={formData.ruleName}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              ruleName: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Description
                        </label>
                        <textarea
                          value={formData.description}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              description: e.target.value,
                            })
                          }
                          rows={3}
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Citation
                          </label>
                          <input
                            type="text"
                            value={formData.citation}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                citation: e.target.value,
                              })
                            }
                            placeholder="e.g., Cal. Civ. Code 1798"
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">
                            Source URL
                          </label>
                          <input
                            type="text"
                            value={formData.sourceUrl}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                sourceUrl: e.target.value,
                              })
                            }
                            className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Applicable Products (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={formData.applicableProducts}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              applicableProducts: e.target.value,
                            })
                          }
                          placeholder="e.g., Mortgage, Auto Loan, Personal Loan"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Compliance Checkpoints (comma-separated)
                        </label>
                        <input
                          type="text"
                          value={formData.complianceCheckpoints}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              complianceCheckpoints: e.target.value,
                            })
                          }
                          placeholder="e.g., Application, Closing, Servicing"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Penalty Info
                        </label>
                        <textarea
                          value={formData.penaltyInfo}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              penaltyInfo: e.target.value,
                            })
                          }
                          rows={2}
                          placeholder="Describe potential penalties for non-compliance"
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">
                          Effective Date
                        </label>
                        <input
                          type="date"
                          value={formData.effectiveDate}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              effectiveDate: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => {
                          setShowForm(false);
                          setEditId(null);
                          setFormData(EMPTY_FORM);
                        }}
                        className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={
                          !formData.stateCode ||
                          !formData.ruleName ||
                          !formData.category ||
                          createMutation.isPending ||
                          updateMutation.isPending
                        }
                        className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white hover:bg-teal-700 disabled:opacity-50"
                      >
                        {createMutation.isPending || updateMutation.isPending
                          ? 'Saving...'
                          : editId
                            ? 'Update Rule'
                            : 'Create Rule'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>

        {/* Delete Confirmation Dialog */}
        <Transition appear show={!!deleteId} as={Fragment}>
          <Dialog
            as="div"
            className="relative z-50"
            onClose={() => setDeleteId(null)}
          >
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-black/30" />
            </Transition.Child>

            <div className="fixed inset-0 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Delete Rule
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-500">
                      Are you sure you want to delete this state compliance rule? This
                      action cannot be undone.
                    </p>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setDeleteId(null)}
                        className="rounded-lg border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => {
                          if (deleteId) deleteMutation.mutate(deleteId);
                        }}
                        disabled={deleteMutation.isPending}
                        className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                      >
                        {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition>
      </div>
    </FeatureGate>
  );
}
